import React, { createContext, useContext, useEffect, useState } from 'react';
import { Company } from '../models/Company';
import { companyService } from '../services/CompanyService';
import { useAuth } from './AuthContext';
import { ensureDefaultCompanyExists } from '../utils/initializeCompany';

interface CompanyContextType {
  company: Company | null;
  loading: boolean;
  refreshCompany: () => Promise<void>;
  updateCompanySettings: (settings: Partial<Company['settings']>) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(
  undefined
);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCompany = async (companyId: string) => {
    try {
      // Ensure default company exists if this is the default company ID
      if (companyId === 'default-company') {
        await ensureDefaultCompanyExists();
      }

      let companyData = await companyService.getCompany(companyId);

      // If company doesn't exist, create it with the given ID
      if (!companyData) {
        companyData = await createMissingCompany(companyId);
      }

      setCompany(companyData);
    } catch (error) {
      console.error('CompanyContext: Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const createMissingCompany = async (companyId: string): Promise<Company> => {
    const now = new Date();
    const company: Company = {
      id: companyId,
      name: 'VERA Health Technologies, LLC',
      email: 'thinkimaedu@gmail.com',
      createdAt: now,
      updatedAt: now,
      subscription: {
        plan: 'premium',
        storageLimit: 100,
        userLimit: 50,
      },
      settings: {
        folderStructure: 'user',
        namingConvention: '{name}_{date}_{vid}',
        retentionDays: 365,
      },
    };

    // Import setDoc and Timestamp for creating the company
    const { doc, setDoc, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../config/firebase');

    const companyRef = doc(db, 'companies', companyId);
    await setDoc(companyRef, {
      ...company,
      createdAt: Timestamp.fromDate(company.createdAt),
      updatedAt: Timestamp.fromDate(company.updatedAt),
    });

    return company;
  };

  useEffect(() => {
    if (user?.companyId) {
      loadCompany(user.companyId);
    } else {
      setCompany(null);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshCompany = async () => {
    if (user?.companyId) {
      await loadCompany(user.companyId);
    }
  };

  const updateCompanySettings = async (
    settings: Partial<Company['settings']>
  ) => {
    if (!user?.companyId) throw new Error('No company ID');
    await companyService.updateSettings(user.companyId, settings);
    await refreshCompany();
  };

  const value: CompanyContextType = {
    company,
    loading,
    refreshCompany,
    updateCompanySettings,
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
